
import type {
  QueryRankingElementRankingDTO,
  QueryRankingPinnedElementDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { BaseQueryRankingModalAcceptEventPayload } from '../../components/search-query-ranking/base-query-ranking-modal.types'
import type { QueryRankingElementActionConfig } from '../../composables/use-query-ranking-element-actions-config'
import type { ToolsMessages } from '../../models/i18n/tools.messages'
import { computed, defineComponent, getCurrentInstance } from 'vue'
import { mapGetters } from '../../composables/utils'
import { PositionEnum } from '../../enums/position.enum'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import Schedule from '../crud/schedule.component.vue'
import { UiAlert, UiModal } from '../keen-ui'
import QueryRankingActionButtons from './query-ranking-action-buttons.component.vue'
import { useQueryRankingModal } from './use-query-ranking-modal'

export default defineComponent({
  name: 'BaseQueryRankingModal',
  components: {
    UiModal,
    UiAlert,
    Schedule,
    QueryRankingActionButtons,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    messages: {
      type: Object as PropType<ToolsMessages['configurations']>,
      required: true,
    },
    disableActions: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    queryRankingConfig: {
      type: Object as PropType<QueryRankingElementRankingDTO | QueryRankingPinnedElementDTO>,
      required: false,
      default: null,
    },
    actions: {
      type: Array as PropType<Array<QueryRankingElementActionConfig['action']>>,
      required: false,
      default: undefined,
    },
    infoMessage: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
  },
  emits: {
    close: () => {},
    accept: (payload: BaseQueryRankingModalAcceptEventPayload) => {},
  },
  setup(props, ctx) {
    const {
      proxy: { $store },
    } = getCurrentInstance()

    const { instanceConfig } = mapGetters($store, CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance,
    })

    const baseClass = 'epb-base-query-ranking-modal'

    const initialFieldValues = {
      schedule: {
        activationDate: undefined,
        deactivationDate: undefined,
      },
    }

    const queryRankingModalComposable = useQueryRankingModal<
      BaseQueryRankingModalAcceptEventPayload,
      string
    >({ resetFields, buildAcceptEventPayload }, ctx)
    const schedule = queryRankingModalComposable.schedule

    const isScheduleValid = computed((): boolean => {
      if (!schedule.value.activationDate && !schedule.value.deactivationDate) {
        return true
      }

      return (
        schedule.value.activationDate &&
        schedule.value.deactivationDate &&
        schedule.value.deactivationDate.getTime() > schedule.value.activationDate.getTime()
      )
    })

    const subtitle = computed((): string | null => {
      if (props.queryRankingConfig) {
        const fieldConfig = instanceConfig.value.catalog.fields.find(
          fieldConfig => fieldConfig.id === props.queryRankingConfig.fieldName,
        )
        return `${fieldConfig?.prettyName ?? props.queryRankingConfig.fieldName}: ${
          props.queryRankingConfig.fieldValue
        }`
      }

      return null
    })

    const actionsDisabled = computed((): boolean => {
      return props.disableActions || !isScheduleValid.value
    })

    function resetFields(): void {
      schedule.value = props.queryRankingConfig
        ? { ...props.queryRankingConfig.schedule }
        : { ...initialFieldValues.schedule }
    }

    function buildAcceptEventPayload(
      action: BaseQueryRankingModalAcceptEventPayload['action'],
    ): BaseQueryRankingModalAcceptEventPayload {
      return {
        schedule: schedule.value,
        action,
      }
    }

    return {
      ...queryRankingModalComposable,
      actionsDisabled,
      baseClass,
      PositionEnum,
      schedule,
      subtitle,
    }
  },
})
